import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase, getConnectionHealth } from '@/utils/supabase';
import { toast } from '@/hooks/use-toast';
import { hasValidSupabaseConfig } from '@/utils/supabaseDebug';

// Helper function to check if an error is related to RLS policies
const isRLSError = (error: any): boolean => {
  if (!error) return false;
  return error.message?.includes('row-level security policy') || 
         error.code === 'PGRST301' || 
         error.message?.includes('permission denied');
};

export interface PortfolioItem {
  id: number;
  _originalId?: number; // Used to track ID changes from frontend to database
  title: string;
  description?: string;
  category?: string;
  client?: string;
  media_type?: string;
  media_url?: string;
  media_path?: string;
  media: {
    type: 'image' | 'video';
    url: string;
    path?: string;
    thumbnail?: string;
  };
  highlight?: boolean;
  created_at?: string;
}

interface DeletePortfolioItemResult {
  success: boolean;
  error?: any;
  deletedFromLocalOnly?: boolean;
}

interface SavePortfolioItemsResult {
  success: boolean;
  error?: any;
  savedToLocalOnly?: boolean;
  message?: string;
  errorDetails?: string;
  updatedIds?: Array<{oldId: number, newId: number}>;
}

export const usePortfolio = (isInitialized: boolean) => {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState(0);
  const loadInProgressRef = useRef<boolean>(false);
  const loadAttemptsRef = useRef<number>(0);
  const initStateChecked = useRef<boolean>(false);
  const [effectiveInitState, setEffectiveInitState] = useState<boolean>(false);

  useEffect(() => {
    const checkAndSetInitState = () => {
      if (initStateChecked.current) return;
      if (isInitialized || hasValidSupabaseConfig()) {
        setEffectiveInitState(true);
        initStateChecked.current = true;
      } else {
        setEffectiveInitState(false);
      }
    };
    checkAndSetInitState();
  }, [isInitialized]);

  const loadPortfolioItems = useCallback(async (forceRefresh = false) => {
    try {
      if (loadInProgressRef.current) return portfolioItems;
      const now = Date.now();
      if (!forceRefresh && now - lastLoadTime < 10000) return portfolioItems;
      if (loadAttemptsRef.current > 3 && !forceRefresh) return portfolioItems;
      setIsLoading(true);
      loadInProgressRef.current = true;
      loadAttemptsRef.current++;
      setLastLoadTime(now);
      const items = await getPortfolioItems();
      if (items && items.length > 0) setPortfolioItems(items);
      else if (items && items.length === 0 && portfolioItems.length > 0) console.log("Received empty items list but keeping existing data");
      else setPortfolioItems(items || []);
      loadInProgressRef.current = false;
      return items;
    } catch (error) {
      console.error("Error loading portfolio items:", error);
      loadInProgressRef.current = false;
      return portfolioItems;
    } finally {
      setIsLoading(false);
    }
  }, [lastLoadTime, portfolioItems]);

  const savePortfolioItems = useCallback(async (items: PortfolioItem[], isAdmin: boolean = false): Promise<SavePortfolioItemsResult> => {
    console.log("Saving portfolio items to localStorage and database", items);
    
    // Save to localStorage first (this always works)
    localStorage.setItem('portfolioItems', JSON.stringify(items));
    setPortfolioItems(items);

    // Save to database if logged in as admin
    if (isAdmin && supabase && effectiveInitState) {
      console.log("User is admin, attempting to save to database", { isAdmin, effectiveInitState });
      
      try {
        // Find existing items
        const { data: existingItems, error: fetchError } = await supabase
          .from('portfolio_items')
          .select('id');
          
        if (fetchError) {
          console.error("Error fetching existing items:", fetchError);
          
          // If this is an RLS error, we can still continue with local storage only
          if (isRLSError(fetchError)) {
            console.log("RLS policy detected - saving to local storage only");
            return { 
              success: true, 
              savedToLocalOnly: true, 
              message: "Saved to local storage only due to database permissions",
              error: fetchError
            };
          }
          
          return { success: false, errorDetails: fetchError.message };
        }
        
        console.log("Existing items in database:", existingItems);
        
        // Process each item
        for (const item of items) {
          // Format data for database
          const itemData = {
            id: item.id,
            title: item.title,
            category: item.category,
            description: item.description,
            client: item.client,
            media_type: item.media_type || (item.media?.type || 'image'),
            media_url: item.media_url || item.media?.url,
            media_path: item.media_path || item.media?.path,
            highlight: item.highlight || false
          };
          
          console.log("Formatted item data for database:", itemData);
          
          // Check if this item exists in database
          const existsInDb = existingItems?.some((dbItem: any) => dbItem.id === item.id);
          
          if (existsInDb) {
            console.log(`Updating existing item ${item.id} in database`);
            const { error: updateError } = await supabase
              .from('portfolio_items')
              .update(itemData)
              .eq('id', item.id);
              
            if (updateError) {
              console.error(`Error updating item ${item.id}:`, updateError);
              
              // If this is an RLS error, log it but continue
              if (isRLSError(updateError)) {
                console.log(`RLS policy prevented updating item ${item.id}, changes saved locally only`);
              }
            } else {
              console.log(`Successfully updated item ${item.id}`);
            }
          } else {
            console.log(`Inserting new item ${item.id} into database`);
            // Clone the itemData but exclude the id field for new inserts
            const { id, ...dataToInsert } = itemData;
            const { data: insertData, error: insertError } = await supabase
              .from('portfolio_items')
              .insert([dataToInsert])
              .select(); // Add select() to return the newly inserted data with DB-assigned ID
              
            if (insertError) {
              console.error(`Error inserting item ${item.id}:`, insertError);
              
              // If this is an RLS error, log it but continue
              if (isRLSError(insertError)) {
                console.log(`RLS policy prevented inserting item ${item.id}, changes saved locally only`);
              }
            } else {
              // If insertion succeeded and we got back the inserted item with its database ID
              if (insertData && insertData.length > 0) {
                const dbItem = insertData[0] as any;
                console.log(`Successfully inserted item, assigned DB ID: ${dbItem.id}`, insertData);
                
                // Store the original ID before changing it
                item._originalId = item.id;
                
                // Update the ID in local state to match the database ID
                item.id = dbItem.id;
                itemData.id = dbItem.id;
                
                // Update the item in localStorage too
                try {
                  const storedItems = localStorage.getItem('portfolioItems');
                  if (storedItems) {
                    const items = JSON.parse(storedItems);
                    const updatedItems = items.map((i: any) => 
                      i.id === id ? { ...i, id: dbItem.id } : i
                    );
                    localStorage.setItem('portfolioItems', JSON.stringify(updatedItems));
                    console.log(`Updated local ID ${id} to database ID ${dbItem.id} in localStorage`);
                  }
                } catch (e) {
                  console.error("Error updating localStorage with new ID", e);
                }
              } else {
                console.log(`Successfully inserted item ${item.id}, but no data returned`);
              }
            }
          }
        }
        
        console.log("Portfolio items saved to database successfully");
        // Check if we have any ID updates to report
        const updatedIdMappings = items
          .filter(item => item._originalId && item._originalId !== item.id)
          .map(item => ({ 
            oldId: item._originalId || item.id, 
            newId: item.id 
          }));
        
        return { 
          success: true,
          updatedIds: updatedIdMappings.length > 0 ? updatedIdMappings : undefined 
        };
      } catch (error: any) {
        console.error("Error saving portfolio items to database:", error);
        
        // If this is an RLS error, we can still continue with local storage only
        if (isRLSError(error)) {
          return { 
            success: true, 
            savedToLocalOnly: true, 
            message: "Saved to local storage only due to database permissions",
            error
          };
        }
        
        return { success: false, errorDetails: error.message };
      }
    } else {
      console.log("Not saving to database - not admin or storage not initialized", { isAdmin, effectiveInitState });
      return { success: true, savedToLocalOnly: true };
    }
  }, [effectiveInitState]);

  const getPortfolioItems = useCallback(async (): Promise<PortfolioItem[]> => {
    try {
      if (hasValidSupabaseConfig()) {
        try {
          const fetchPromise = supabase.from('portfolio_items').select('*');
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject({ data: null, error: new Error('Request timed out') }), 5000));
          const response: any = await Promise.race([fetchPromise, timeoutPromise]).catch(error => ({ data: null, error }));
          const fetchError = response?.error ?? null;
          const fetchData = response?.data ?? null;

          if (fetchError) console.log("Error fetching portfolio data:", fetchError);
          else if (fetchData && fetchData.length > 0) {
            const items = fetchData.map((item: any) => ({
              id: item.id,
              title: item.title,
              description: item.description || '',
              category: item.category || 'content',
              client: item.client || '',
              media_type: item.media_type || 'image',
              media_url: item.media_url || '',
              media_path: item.media_path || '',
              media: {
                type: item.media_type || 'image',
                url: item.media_url || '',
                path: item.media_path || ''
              },
              highlight: item.highlight || false,
              created_at: item.created_at
            }));
            localStorage.setItem('portfolioItems', JSON.stringify(items));
            return items;
          }
        } catch (err) {
          console.error('Exception in portfolio fetch from Supabase:', err);
        }
      }
      const storedItems = localStorage.getItem('portfolioItems');
      let localItems: PortfolioItem[] = [];
      if (storedItems) try { localItems = JSON.parse(storedItems); } catch { }
      return localItems;
    } catch {
      return [];
    }
  }, []);

  const deletePortfolioItem = useCallback(async (itemId: number, isAdmin: boolean = false): Promise<DeletePortfolioItemResult> => {
    try {
      if (!isAdmin && !import.meta.env.DEV) return { success: false, error: 'Not authorized' };
      setPortfolioItems(prev => prev.filter(item => item.id !== itemId));
      const storedItems = localStorage.getItem('portfolioItems');
      if (storedItems) {
        const currentItems: PortfolioItem[] = JSON.parse(storedItems);
        const updatedItems = currentItems.filter(item => item.id !== itemId);
        localStorage.setItem('portfolioItems', JSON.stringify(updatedItems));
      }
      if (!effectiveInitState) return { success: true };

      try {
        const deletePromise = supabase.from('portfolio_items').delete().eq('id', itemId);
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject({ error: new Error('Delete request timed out') }), 5000));
        const result: any = await Promise.race([deletePromise, timeoutPromise]).catch(err => ({ error: err }));
        const { error } = result;
        if (isRLSError(error)) return { success: true, deletedFromLocalOnly: true };
        if (error) throw error;
      } catch (err) {
        return { success: true, deletedFromLocalOnly: true, error: err };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err };
    }
  }, [effectiveInitState]);

  return {
    portfolioItems,
    isLoading,
    savePortfolioItems,
    getPortfolioItems,
    deletePortfolioItem,
    loadPortfolioItems,
  };
};
